



























































































import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import Rep from '@/_shared/entities/rep/rep.entity';
import Language from '@/_shared/entities/language.entity';
import CountriesService from '@/_shared/services/countries.service';
import LogService from '@/_shared/services/log/log.service';
import SessionMessageService from '@/_shared/services/session-message.service';
import MyProfileService from '@/profile/my-profile/services/my-profile.service';
import UserPhoto from '@/_shared/components/rep/user-photo.component.vue';
import RepLocale from '@/_shared/entities/rep/rep-locale.entity';
import { BFormFile } from 'bootstrap-vue';
import { AxiosResponse } from 'axios';

@Component({
  components: {
    UserPhoto,
    BFormFile,
  },
})
export default class MyProfileView extends Vue {
  private selectedLanguage: number | null = null;
  private languages: Language[] = [];
  private recognitionName = '';
  private selectedPicture: File | null = null;
  private showDisclaimer = false;
  private hasFinishedLoading = false;

  $refs!: {
    pictureInput: HTMLFormElement;
  }

  get rep(): Rep {
    return this.$store.state.RepStore.rep;
  }

  get repLocale(): RepLocale {
    return this.$store.state.RepStore.repLocale;
  }

  get userName(): string {
    return `${this.rep.firstName} ${this.rep.lastName}`;
  }

  get recognitionNamePlaceholder(): string {
    return this.$t('PROFILE.CHANGE_PASSWORD.RECOGNITION_NAME_PLACEHOLDER').toString();
  }

  private async created(): Promise<void> {
    this.$store.dispatch('showLoading');
    this.recognitionName = this.rep.recognitionName;
    this.selectedLanguage = this.repLocale?.localeId;
    try {
      const countryCode = await this.getRepCountryCode();
      await this.retrieveLanguages(countryCode);
      await this.retrieveRecognitionApprovalState();
    } finally {
      this.$emit('done-loading');
      this.hasFinishedLoading = true;
      this.$store.dispatch('hideLoading');
      this.showSessionMessages();
    }
  }

  private async getRepCountryCode(): Promise<string> {
    return this.$store.dispatch('getCountryCodeByCountryName', this.rep.shipping.country);
  }

  private retrieveLanguages(countryCode: string): Promise<void> {
    return CountriesService.getCountryLanguages(countryCode)
      .then((languages) => {
        this.languages = languages;
      });
  }

  private async retrieveRecognitionApprovalState(): Promise<void> {
    await MyProfileService.getRecognitionPictureApprovalState(this.rep.did)
      .then((data) => {
        this.showDisclaimer = data.approved === 0;
      })
      .catch(() => {
        this.showDisclaimer = false;
      });
  }

  private selectPicture(): void {
    this.$refs.pictureInput.$el.childNodes[0].click();
  }

  validateImage(): void {
    if (!this.selectedPicture) return;
    const isImage = this.isSelectedPictureAnImage();
    const acceptedSizeInBytes = 10000000;
    const imageSize = this.selectedPicture.size || 0;

    if (!isImage) {
      LogService.toastrError(this.$t('PROFILE.RECOGNITION_PICTURE.FILE_TYPE_ERROR').toString());
      return;
    }
    if (imageSize > acceptedSizeInBytes) {
      LogService.toastrError(this.$t('PROFILE.RECOGNITION_PICTURE.FILE_SIZE_ERROR').toString());
      return;
    }
    this.uploadImage();
  }

  private isSelectedPictureAnImage() {
    if (!this.selectedPicture) return false;
    const acceptedFileFormats = ['png', 'jpg', 'jpeg'];
    return acceptedFileFormats
      .some((format: string) => this.selectedPicture?.type.includes(format));
  }

  private uploadImage(): void {
    if (this.selectedPicture === null) {
      return;
    }

    this.$store.dispatch('showLoading');
    const formData = new FormData();
    formData.append('picture', this.selectedPicture);

    MyProfileService.changeRecognitionPicture(this.rep.did, formData)
      .then(() => {
        this.selectedPicture = null;
        LogService.toastrSuccess(this.$t('PROFILE.TOAST_SUCCESS_MESSAGE').toString());
      })
      .catch((error) => {
        this.showErrorMessage(error.response.data.error);
        this.$store.dispatch('hideLoading');
      })
      .finally(() => {
        this.retrieveRecognitionApprovalState();
        this.$store.dispatch('hideLoading');
      });
  }

  private async saveData(): Promise<void> {
    this.$store.dispatch('showLoading');
    let refreshPage = false;
    const savePromises = [];
    if (this.recognitionName !== this.rep.recognitionName) {
      savePromises.push(this.saveRecognitionName());
    }
    if (this.selectedLanguage !== this.repLocale?.localeId) {
      savePromises.push(this.savePreferredLanguage());
      refreshPage = true;
    }
    Promise.all(savePromises)
      .then(() => this.handleSaveFulfillment(refreshPage))
      .catch(() => this.handleSaveError(refreshPage))
      .finally(() => {
        this.retrieveRecognitionApprovalState();
        this.$store.dispatch('hideLoading');
      });
  }

  private handleSaveError(refreshPage: boolean): void {
    if (refreshPage) {
      SessionMessageService.registerErrorMessage('PROFILE.TOAST_ERROR_MESSAGE');
      SessionMessageService.storeMessages();
      this.refreshPage();
      return;
    }
    this.recognitionName = this.rep.recognitionName;
    LogService.toastrError(this.$t('PROFILE.TOAST_ERROR_MESSAGE').toString());
  }

  private handleSaveFulfillment(refreshPage: boolean): void {
    if (refreshPage) {
      SessionMessageService.registerSuccessfulMessage('PROFILE.TOAST_SUCCESS_MESSAGE');
      SessionMessageService.storeMessages();
      this.refreshPage();
      return;
    }
    this.recognitionName = this.rep.recognitionName;
    LogService.toastrSuccess(this.$t('PROFILE.TOAST_SUCCESS_MESSAGE').toString());
  }

  private savePreferredLanguage(): Promise<AxiosResponse<void>> {
    const languageId = this.selectedLanguage ?? this.rep.localeId;
    return MyProfileService.changePreferredLanguage(this.rep, languageId);
  }

  private saveRecognitionName(): Promise<AxiosResponse<void>> {
    return MyProfileService.changeRecognitionName(this.rep.did, this.recognitionName);
  }

  private refreshPage(): void {
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  private showSessionMessages(): void {
    const sessionMessages = SessionMessageService.retrieveMessages();
    for (let i = 0; i < sessionMessages.length; i += 1) {
      const message = sessionMessages[i];
      if (message.type === 'success') {
        this.showSuccessMessage(message.content);
      } else {
        this.showErrorMessage(message.content);
      }
    }
  }

  private showSuccessMessage(message: string): void {
    LogService.toastrSuccess(
      this.$t(message).toString(),
      this.$t('PROFILE.TOAST_SUCCESS_HEADER').toString(),
    );
  }

  private showErrorMessage(message: string): void {
    LogService.toastrError(
      this.$t(message).toString(),
      this.$t('PROFILE.TOAST_ERROR_HEADER').toString(),
    );
  }
}
